/**
 * NetworkStatus Component
 * Detects and displays network connectivity status
 * Shows banner when offline, auto-dismisses when back online
 * 
 * SSR-Safe: Uses null initial state to prevent hydration mismatch
 */

import { useState, useEffect } from 'react';
import { Banner } from '@shopify/polaris';

/**
 * Network Status indicator for offline detection
 *
 * This component monitors the browser's network connectivity using the Navigator Online API
 * and displays a critical Banner when the connection is lost. It automatically dismisses
 * the banner when connectivity is restored.
 *
 * The component is particularly useful for embedded Shopify apps to notify merchants when
 * network issues might prevent API calls or data synchronization.
 *
 * @example
 * ```tsx
 * // In root.tsx or layout component
 * import { NetworkStatus } from '~/components/NetworkStatus';
 *
 * export default function Root() {
 *   return (
 *     <Frame>
 *       <NetworkStatus />
 *       <Outlet />
 *     </Frame>
 *   );
 * }
 * ```
 */
export function NetworkStatus() {
  // Initialize to null to prevent hydration mismatch
  // Server doesn't have navigator.onLine, so we defer to client
  const [isOnline, setIsOnline] = useState<boolean | null>(null);

  useEffect(() => {
    // Initialize with current network status after hydration
    setIsOnline(navigator.onLine);

    // Event handlers for online/offline status changes
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    // Listen for connectivity changes
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Don't render during SSR (isOnline is null) or when online
  if (isOnline === null || isOnline) {
    return null;
  }

  return (
    <div
      className="banner-enter"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1001, // Above navigation progress (1000)
      }}
    >
      <Banner tone="critical">
        You are currently offline. Some features may not work until your connection is restored.
      </Banner>
    </div>
  );
}
